import { useCallback, useEffect, useState } from "react";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import { type WishlistModel } from "~/features/wishlist/types/model";
import { useDispatch, useSelector } from "react-redux";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { sendWishlist } from "~/domains/salesPartner/api/sendWishlist";
import { sendLinearConfigurations } from "~/domains/salesPartner/api/sendLinearConfigurations";
import { Trans, useTranslation } from "react-i18next";
import { Link, LinkButton } from "~/common/components/Link";

import { type GoogleMarker, type SalesPartnerAddress } from "~/domains/salesPartner/types/SalesPartnerTypes";
import { useIsMaxTabletSize, useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CART_PAGE, PRIVACY_PAGE } from "~/utils/url/staticUrls";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Box, Button, styled, TextField } from "~/common/mui";
import { trackShareWishlistThunk } from "~/features/salesPartner/redux/actions";
import { selectShareableWishlists } from "~/features/salesPartner/redux/selectors";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { IMG_SALES_PARTNER_FINDER_CART } from "~/utils/url/staticResources";
import { Alert } from "~/common/mui/Alert/Alert";
import { type SalesPartnerSearchFormValues, trackPartnerFinderSearch } from "~/app/analytics/ga/salesPartner";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

interface Props {
  shareLinearConfigurationData?: string[];
  companyDetails: SalesPartnerAddress;
  company: GoogleMarker;
  searchValues: SalesPartnerSearchFormValues;
  close?: () => void;
}

const RightSide = styled(Box)(({ theme }) => ({
  margin: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "100%",
}));

const BorderTopHeading = styled("h3")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    borderTop: 0,
    paddingTop: 0,
  },
}));

const ListInside = styled("ol")(({ theme }) => ({
  gap: theme.spacing(2.5),
  padding: 0,
  listStylePosition: "inside",
  marginBottom: theme.spacing(7.5),
  "& li": {
    margin: `${theme.spacing(1.25)} 0`,
  },
}));

const SPCompanyDetailsCardRightRegistered = (props: Props) => {
  const { t } = useTranslation();
  const { companyDetails } = props;

  const wishlists = useSelector(selectShareableWishlists);
  const dispatch = useDispatch();
  const trackShareWishlist = useCallback((salesPartnerName: string, wishlistId: string) => {
    dispatch(trackShareWishlistThunk(salesPartnerName, wishlistId));
  }, []);

  const [wishlistId, setWishlistId] = useState<string | null | undefined>(() =>
    new URLSearchParams(window.location.search).get("wl-id"),
  );
  const [wishlistEntryIds] = useState(
    () => new URLSearchParams(window.location.search).get("wl-entry-ids")?.split(",") ?? [],
  );

  const { setFalse: hideError, setTrue: showError, val: hasError } = useBooleanState(false);
  const [wlValid, setWlValid] = useState(false);

  const { val: checkboxState, toggle: toggleCheckbox } = useBooleanState(false);
  const [comment, setComment] = useState("");

  const spHasEmail = !!companyDetails.email;

  useEffect(() => {
    setWlValid(!!wishlists.find((wishlist: WishlistModel) => wishlist.id === wishlistId));
  }, [wishlistId]);

  let sharedData = null;
  let submitHandler = null;
  let title = null;

  const hasLinearData = props.shareLinearConfigurationData && props.shareLinearConfigurationData.length > 0;

  if (props.shareLinearConfigurationData && props.shareLinearConfigurationData.length) {
    title = t("salesPartner.gmap.card.rightRegistered.title");
    sharedData = (
      <div className={"shared-linear-configs"}>
        {t("page.store.finder.label.shared.configurations", {
          configurationsCount: props.shareLinearConfigurationData.length,
        })}
      </div>
    );
    submitHandler = async () => {
      if (!checkboxState || !props.shareLinearConfigurationData) {
        showError();
        return;
      }
      await sendLinearConfigurations({
        sharedConfigurations: props.shareLinearConfigurationData,
        salesPartnerCode: companyDetails.salesPartnerId,
        salesPartnerMessage: comment,
      });
      redirectToUrl("/store-finder/confirmation");
    };
  } else {
    const isFromWishlistPage = !!wishlistEntryIds.length;
    let adjustedWishlists;
    if (isFromWishlistPage) {
      title = t("page.salesPartnerFinder.share.requestOfferSelected.title");
      adjustedWishlists = wishlists.map((wl: WishlistModel) => ({
        ...wl,
        entryIds: wl.id === wishlistId ? wishlistEntryIds : wl.entryIds,
      }));
    } else {
      title = t("salesPartner.gmap.card.rightRegistered.title");
      adjustedWishlists = wishlists;
    }

    sharedData = (
      <WishlistDropdown
        className={"scha__sales-partner__gmaps-sp-card__right__send-wl__wishlist-input"}
        label={t("page.salesPartnerFinder.share.requestOfferSelected.dropdown")}
        wishlists={adjustedWishlists}
        selectedWishlist={wishlistId}
        onWishlistSelect={setWishlistId}
        disabled={!!wishlistEntryIds.length}
      />
    );
    submitHandler = async () => {
      if (!checkboxState || !wishlistId || !wlValid) {
        showError();
        return;
      }
      hideError();

      trackShareWishlist(companyDetails.companyNameDisplay, wishlistId!);
      const selectedWishlist = wishlists.find((wl: WishlistModel) => wl.id === wishlistId)!;
      const entryIds = wishlistEntryIds.length > 0 ? wishlistEntryIds : selectedWishlist.entryIds;
      await sendWishlist({
        wishlistId: wishlistId!,
        entryIds: entryIds,
        salesPartnerCode: companyDetails.salesPartnerId,
        salesPartnerMessage: comment,
      });
      redirectToUrl("/store-finder/confirmation");
    };
  }

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMaxTabletSize();

  const normal = (
    <div className="scha__sales-partner__gmaps-sp-card__right__send-wl">
      <h3 className="scha__sales-partner__gmaps-sp-card__right__send-wl__title">{title}</h3>

      {sharedData}

      <div className="scha__sales-partner__gmaps-sp-card__right__send-wl__comment">
        <TextField
          variant={"outlined"}
          multiline
          fullWidth
          minRows={3}
          maxRows={10}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          label={t("salesPartner.gmap.card.rightRegistered.message-label")}
          data-test-id={"sales-partner_wl-comment"}
        />
      </div>
      <CheckboxWithLabel
        data-test-id={"checkbox-accept-privacy"}
        checked={checkboxState}
        onCheck={() => {
          toggleCheckbox();
          hideError();
        }}
        error={hasError}
        className={"scha__sales-partner__gmaps-sp-card__right__send-wl__checkbox"}
      >
        <Trans i18nKey="salesPartner.message.checkbox">
          I hereby agree to forwarding contact information from my user profile and my comments to the selected sales
          partner for the purpose of contacting and requesting an offer.
          <Link href={PRIVACY_PAGE} target="_blank">
            Privacy Policy
          </Link>
        </Trans>
      </CheckboxWithLabel>
      {hasError && (
        <div className="mb-3">
          <Alert severity="error">{t("salesPartner.gmap.card.rightRegistered.errorMessage")}</Alert>
        </div>
      )}

      <div className="scha__sales-partner__gmaps-sp-card__right__send-wl__actions">
        <div> {t("salesPartner.mandatory")}</div>

        <Button
          fullWidth={false}
          data-test-id={"sales-pratner-register-for-free"}
          disabled={!spHasEmail}
          onClick={submitHandler}
        >
          {t("salesPartner.gmap.card.btn.send")}
        </Button>

        {!isDesktop && (
          <LinkButton className={"scha__sales-partner__gmaps-sp-card__right__send-wl__cancel"} onClick={props.close}>
            {t("cancel")}
          </LinkButton>
        )}
      </div>
    </div>
  );

  const upgraded = (
    <div className="scha__sales-partner__gmaps-sp-card__right__send-wl">
      <BorderTopHeading>{t("page.salesPartnerFinder.share.requestOfferSelected.title")}</BorderTopHeading>
      <div className="scha__sales-partner__gmaps-sp-card__right__register">
        {!isTablet && (
          <div className="scha__sales-partner__gmaps-sp-card__right__register__img-container">
            <img className="ml-3" src={IMG_SALES_PARTNER_FINDER_CART} alt="cart" />
          </div>
        )}
        <RightSide>
          <ListInside>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.2step"} />
            </li>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.3step"} />
            </li>
          </ListInside>
          {isDesktop && (
            <Button
              href={CART_PAGE}
              startIcon={<ShoppingCartIcon  />}
              fullWidth
              onClick={() => {
                trackPartnerFinderSearch(
                  props.searchValues,
                  "Go To Cart Intended",
                  "Click",
                  transformSelectedMarkerData(t, props.company),
                );
              }}
            >
              {t("addToCartPopup.checkoutBtn")}
            </Button>
          )}
        </RightSide>
      </div>
    </div>
  );

  if (hasLinearData) {
    return normal;
  }

  return userHasPermission(Permission.ADD_TO_CART) ? upgraded : normal;
};

export default SPCompanyDetailsCardRightRegistered;
